import React, { useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Search, RefreshCw, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const PagedJobFilesTable = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [readyFilter, setReadyFilter] = useState(''); // '', 'yes', 'no'
  const [jobTypeFilter, setJobTypeFilter] = useState(''); // '', 'apollo', 'lead_finder'
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate()-7); return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchPage = async (p=page, sBy=sortBy, sDir=sortDir, size=pageSize, q=search, rf=readyFilter, sd=startDate, ed=endDate, jt=jobTypeFilter) => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/reports/job-files/paged`);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(size));
      url.searchParams.set('sort_by', sBy); url.searchParams.set('sort_dir', sDir);
      if (q && q.trim()) url.searchParams.set('q', q.trim());
      if (rf) url.searchParams.set('ready', rf);
      if (jt) url.searchParams.set('job_type', jt);
      if (sd && ed) {
        url.searchParams.set('start_date', new Date(sd + 'T00:00:00Z').toISOString());
        url.searchParams.set('end_date', new Date(ed + 'T23:59:59Z').toISOString());
      }
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${adminToken}` } });
      const data = await resp.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 50);
      setSortBy(data.sort_by || sBy);
      setSortDir(data.sort_dir || sDir);
    } catch (e) {
      console.error('job-files fetch failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPage(1, 'created_at', 'desc', 50, ''); }, []);

  const grid = '0.9fr 1.4fr 1fr 1.2fr 0.7fr 0.8fr 0.7fr 1fr 0.8fr';
  const headerCell = (label, key) => (
    <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
      onClick={() => { const next = sortBy===key && sortDir==='desc' ? 'asc':'desc'; setSortBy(key); setSortDir(next); fetchPage(page, key, next); }}>
      {label} {sortBy===key ? (sortDir==='desc' ? '▼' : '▲') : ''}
    </div>
  );

  const adminDownload = async (jobId, jobType = 'apollo') => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/api/admin/jobs/${jobId}/download?job_type=${jobType}`, { headers: { 'Authorization': `Bearer ${adminToken}` } });

      if (resp.status === 202) {
        const data = await resp.json();
        return alert('Preparing file. Please retry in a few seconds.');
      }

      if (!resp.ok) {
        return alert(`Download failed: ${resp.statusText}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = resp.headers.get('content-disposition');
      let filename = 'download.xlsx';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Download file blob
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Download failed:', e);
      alert('Download failed');
    }
  };

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Card.Title>Job Files</Card.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input type="date" value={startDate} onChange={(e)=>setStartDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
            <input type="date" value={endDate} onChange={(e)=>setEndDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
            <select value={readyFilter} onChange={(e)=>setReadyFilter(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
              <option value="">All Files</option>
              <option value="yes">Ready</option>
              <option value="no">Not Ready</option>
            </select>
            <select value={jobTypeFilter} onChange={(e)=>setJobTypeFilter(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
              <option value="">All Jobs</option>
              <option value="apollo">Apollo</option>
              <option value="lead_finder">Lead Finder</option>
            </select>
            <div style={{ position: 'relative' }}>
              <input placeholder="Search email, list, job id" value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter') fetchPage(1, sortBy, sortDir, pageSize, e.target.value, readyFilter); }} style={{ padding: '8px 28px 8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: 320 }} />
              <Search size={14} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
            </div>
            <Button variant="secondary" onClick={()=>fetchPage(1, sortBy, sortDir, pageSize, search, readyFilter, startDate, endDate, jobTypeFilter)} icon={<RefreshCw size={14} />}>Filter</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX:'auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:grid, borderBottom:'1px solid #e5e7eb', minWidth:1080 }}>
            {headerCell('Date','created_at')}
            <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280' }}>User Email</div>
            <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280' }}>Job ID</div>
            <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280' }}>List Name</div>
            <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280' }}>Type</div>
            {headerCell('Status','status')}
            {headerCell('Results','result_count')}
            {headerCell('Ready At','s3_ready_at')}
            <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280' }}>Actions</div>
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:'center', color:'#6b7280' }}>Loading...</div>
          ) : items.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:'#6b7280' }}>No rows</div>
          ) : (
            items.map(row => (
              <div key={row.job_id} style={{ display:'grid', gridTemplateColumns:grid, borderBottom:'1px solid #f3f4f6', alignItems:'center', minWidth:1080 }}>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280', whiteSpace:'nowrap' }}>{row.date}</div>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.user_email}</div>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#111827' }}>{(row.job_id||'').slice(0,8)}...</div>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.list_name}</div>
                <div style={{ padding:'10px 12px' }}>
                  <span style={{ padding:'2px 8px', borderRadius:999, fontSize:10, fontWeight:600, color: row.job_type==='lead_finder' ? '#7c3aed' : '#0369a1', backgroundColor: row.job_type==='lead_finder' ? '#f3e8ff' : '#e0f2fe' }}>
                    {row.job_type==='lead_finder' ? 'Lead Finder' : 'Apollo'}
                  </span>
                </div>
                <div style={{ padding:'10px 12px' }}>
                  <span style={{ padding:'2px 8px', borderRadius:999, fontSize:11, fontWeight:600, color: row.status==='completed' ? '#166534' : row.status==='failed' ? '#991b1b' : '#92400e', backgroundColor: row.status==='completed' ? '#dcfce7' : row.status==='failed' ? '#fee2e2' : '#fef3c7' }}>{row.status}</span>
                </div>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280', whiteSpace:'nowrap' }}>{row.result_count}</div>
                <div style={{ padding:'10px 12px', fontSize:12, color:'#6b7280', whiteSpace:'nowrap' }}>{row.s3_ready_at || (row.s3_ready ? 'Ready' : '')}</div>
                <div style={{ padding:'10px 12px' }}>
                  <Button size="sm" disabled={!row.s3_ready} onClick={()=>adminDownload(row.job_id, row.job_type || 'apollo')} icon={<Download size={14} />}>Download</Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
          <div style={{ fontSize:12, color:'#6b7280' }}>Page {page} of {Math.ceil(total/pageSize)||1} • {total} total</div>
          <div style={{ display:'flex', gap:8 }}>
            <Button variant="secondary" onClick={()=>{ if(page>1){ const np=page-1; setPage(np); fetchPage(np); } }} disabled={page===1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={()=>{ const maxp=Math.ceil(total/pageSize)||1; if(page<maxp){ const np=page+1; setPage(np); fetchPage(np); } }} disabled={page>=(Math.ceil(total/pageSize)||1)} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default PagedJobFilesTable;